import React, { useState } from 'react';
import { Circle } from 'lucide-react';
import { ObservableLevelEnumValue, TaskStatus } from '../../data/enums';
import StickyComponent from '../atoms/StickyComponent';
import { Label } from '../atoms/Label';
import useStaticImage from '../../hooks/useStaticImage';
import TableOfContensItemRenderer from './TableOfContentsItemRenderer';
import { LoadingIndicator } from '../atoms/LoadingIndicator';
import { Spinner } from '../atoms/Spinner';

// Add the HoverOverMessage component
interface HoverOverMessageProps {
  text: string;
  children: React.ReactNode;
}

export const HoverOverMessage: React.FC<HoverOverMessageProps> = ({
  text,
  children,
}) => {
  const [isVisible, setIsVisible] = useState(false);

  return (
    <div
      className="relative w-full"
      onMouseEnter={() => setIsVisible(true)}
      onMouseLeave={() => setIsVisible(false)}
    >
      {children}
      {isVisible && text && (
        <div className="absolute bottom-full left-1/2 z-10 mb-1 -translate-x-1/2 rounded-sm bg-gray-800 px-3 py-2 text-sm whitespace-nowrap text-white shadow-lg">
          {text}
          <div className="absolute -bottom-1 left-1/2 size-2 -translate-x-1/2 rotate-45 transform bg-gray-800"></div>
        </div>
      )}
    </div>
  );
};

type Status = TaskStatus;

export interface TableOfContentsItem {
  id: string;
  order: number;
  label: string;
  status: Status;
  url: string;
  enabled: boolean;
  disabledMessage?: string;
  inspectionId: string;
  tooltipKey?: string;
  inspectionCategoryTypeId: string;
  multiselected?: boolean;
  locationState?: any;
  showErrorIcon?: boolean;
  disabledModal?: {
    title: string;
    message?: string;
    children?: React.ReactNode;
  };
}

export interface TableOfContentsSection {
  id: string;
  order: number;
  title: string;
  multiselect?: boolean;
  items: TableOfContentsItem[];
  tooltipKey?: string;
}

export interface InspectionScreenData {
  inspectionId: string;
  inspectionCategoryTypeId: string;
  level: ObservableLevelEnumValue;
  entityId: number;
}

export const StatusIcon = ({ status }: { status: Status }) => {
  const taskCompleteImage = useStaticImage('images/task_complete.png');
  const taskStartedImage = useStaticImage('images/task_started.png');
  const taskStargedNoTickImage = useStaticImage(
    'images/task_started_no_tick.png',
  );
  switch (status) {
    case TaskStatus.completed:
      return <img src={taskCompleteImage} className="size-5" alt="Completed" />;
    case TaskStatus.inProgress:
      return (
        <img src={taskStartedImage} className="size-5" alt="In Progress" />
      );
    case TaskStatus.isLoading:
      return (
        <Spinner iconSrc={taskStargedNoTickImage} className="text-red-500" />
      );
    default:
      return <Circle className="size-5 text-inkDisabled" />;
  }
};

interface TableOfContentsRendererProps {
  sections: TableOfContentsSection[];
  header?: React.ReactNode;
  footer?: React.ReactNode;
  onMultiSelectChange?: (item: TableOfContentsItem) => void;
}

export const TableOfContentsRenderer = ({
  sections,
  header,
  footer,
  onMultiSelectChange,
}: TableOfContentsRendererProps) => {
  return (
    <div className="flex flex-col">
      {/* Header */}
      {header && (
        <StickyComponent className="flex-none">{header}</StickyComponent>
      )}

      {/* Scrollable Content */}
      <div className="mt-4 flex-1 overflow-y-auto">
        {sections
          .sort((a, b) => a.order - b.order)
          .map((section) => (
            <div key={section.id} className="mb-6">
              {section.title && (
                <>
                  <div>
                    <Label
                      titleType="subHeader"
                      tooltipKey={section.tooltipKey}
                      className="py-2"
                    >
                      <HoverOverMessage
                        text={section.tooltipKey ?? section.title}
                      >
                        <span className="block w-full">{section.title}</span>
                      </HoverOverMessage>
                    </Label>
                  </div>
                </>
              )}
              <div className="bg-white">
                {section.items
                  .sort((a, b) => a.order - b.order)
                  .map((item) => (
                    <TableOfContensItemRenderer
                      key={item.id}
                      item={item}
                      section={section}
                      onMultiSelectChange={onMultiSelectChange}
                    />
                  ))}
              </div>
            </div>
          ))}
      </div>
      <hr className="-mx-4 border-line" />
      {footer}
    </div>
  );
};
